// library functions
import { Elastic, Expo, Sine } from "gsap/EasePack";
import { TimelineLite } from "gsap/TimelineLite";
import { TimelineMax } from "gsap/TimelineMax";
import { TweenLite } from "gsap/TweenLite";
import { Emitter } from "pixi-particles";

// @ts-ignore
import {BezierPlugin} from "gsap/BezierPlugin";
import { getTexture, makeSprite } from "html-living-framework/lib/Utils/PixiUtils";

// singletons
import { Application } from "Application";
import { GameState } from "Models/GameState";

// gameobjects
import { ExteriorHUD } from "GameObjects/GameExterior/ExteriorHUD";
import type { HiddenItem } from "GameObjects/GameExterior/HiddenItem";
import { HidingSpot } from "GameObjects/GameExterior/HidingSpot";
import { Viewport } from "vendor/viewport.es.js";

// framework stuff
import * as HLF from "html-living-framework";

// pubsub topics
import * as Topics from "Data/Topics";

// static data
import * as Constants from "Data/Constants";
import { GameConfig } from "Data/GameConfig";
import * as VO from "Data/VO";

// types and interfaces
import type { BGConfig } from "Data/BGConfig";
import type { HidingSpotID, ItemID, LocationID } from "Data/GameConfig";
import type { IHiddenItemConfig } from "Interfaces/IHiddenItemConfig";
import type { IHidingSpotState } from "Interfaces/IHidingSpotState";
import type { IItemState } from "Interfaces/IItemState";
import type { ILevelState } from "Interfaces/ILevelState";
import type { IVoiceoverConfig } from "Interfaces/IVoiceoverConfig";
import { addBG, addSFX, addVO } from "Utils/AssetUtils";

// shorthands (private static constants)
const fnt = Constants.FONT_GEL_MED_22;
const ss = Constants.SPRITESHEET_UI;
const STATE_GAME_EXTERIOR: string = "State_GameExterior";
const IDLE_DELAY = 0;
const IDLE_REPEAT_DELAY = 10;

const TRIGGER_IDLE_AFTER_CLICKS = 5;

// this is for webpack
const dontTreeShake = [BezierPlugin];

export class GameExterior extends HLF.State {

    public static readonly Assets: HLF.AssetMapData[] = [
        new HLF.AssetMapData(fnt, HLF.AssetType.FONT),
        ...ss.map((s) => new HLF.AssetMapData(s, HLF.AssetType.TEXTURE_ATLAS)),
        new HLF.SpineAsset("luna", HLF.AssetType.SPINE_JSON, "luna"),
        new HLF.AudioAsset("blink", HLF.AudioCategory.SFX),
        // new HLF.AudioAsset("woosh-quick-1", HLF.AudioCategory.SFX),
        new HLF.AudioAsset("woosh-quick-2", HLF.AudioCategory.SFX),
        new HLF.AudioAsset("found", HLF.AudioCategory.SFX),
        new HLF.AudioAsset("into-hat-2", HLF.AudioCategory.SFX),
        new HLF.AudioAsset(VO.LUNA.A1, HLF.AudioCategory.VO),
        new HLF.AudioAsset(VO.LUNA.A2, HLF.AudioCategory.VO),
        new HLF.AudioAsset(VO.LUNA.A3, HLF.AudioCategory.VO),
        new HLF.AudioAsset(VO.LUNA.A4, HLF.AudioCategory.VO),
        new HLF.AudioAsset(VO.LUNA.A5, HLF.AudioCategory.VO),
        new HLF.AudioAsset(VO.LUNA.A6, HLF.AudioCategory.VO),
        new HLF.AudioAsset(VO.LUNA.A7, HLF.AudioCategory.VO),
        new HLF.AudioAsset(VO.LUNA.A8, HLF.AudioCategory.VO),
        new HLF.AudioAsset(VO.LUNA.A9, HLF.AudioCategory.VO),
        new HLF.AudioAsset(VO.LUNA.A12, HLF.AudioCategory.VO),
        new HLF.AudioAsset(VO.LUNA.A13, HLF.AudioCategory.VO),
        new HLF.AudioAsset(VO.LUNA.A14, HLF.AudioCategory.VO),
        new HLF.AudioAsset(VO.LUNA.A15, HLF.AudioCategory.VO),
    ];

    protected clickParticles?: Emitter;
    protected hintParticles?: Emitter;
    protected bg: PIXI.Container = new PIXI.Container();
    protected viewport?: Viewport;
    protected viewportWrapper = this.addChild(new PIXI.Container());
    protected viewportContentWrapper?: PIXI.Container;
    protected hud: ExteriorHUD = this.addChild(new ExteriorHUD());

    protected _subscriptions: any[] = [];
    protected margin: { left: number; right: number; } = {left: 0, right: 0};
    protected worldWidth: number = 0;

    protected id!: LocationID;
    protected items: Partial<Record<ItemID, HiddenItem>> = {};
    protected hidingSpots: Partial<Record<HidingSpotID, HidingSpot>> = {};

    protected itemFoundTimeline?: TimelineMax;
    protected hintTimeline?: TimelineLite;
    protected idleTimeline?: TimelineMax;
    protected vo?: IVoiceoverConfig;
    protected bgm?: HLF.IAudioTrack;
    private _lastIdleVO: string = "";
    private _clickCount: number = 0;
    private _lastIdleDirectionVO: string = "";
    private _lastIdleFactVO: string = "";

    public static GetIdForState(id: LocationID, narrative: number, variation: number) {
        return `${STATE_GAME_EXTERIOR}_${id}_${narrative}_${variation}`;
    }

    public static GetAssetsForState(pID: LocationID, narrative: number,  variation: number): HLF.AssetMapData[] {
        const locationConfig = GameConfig.locations[pID];
        const narrativeConfig = locationConfig.narratives[narrative];
        const narrativeVariationConfig = narrativeConfig.variations[variation];

        const bg = locationConfig.exterior.bg;
        const hiddenitems = narrativeVariationConfig.hiddenitems as IHiddenItemConfig[];
        const itemIDs: ItemID[] = hiddenitems.map((h) => (typeof(h.item) === "string" ? h.item : h.item.id) as ItemID);
        const vo = {...narrativeConfig.vo, ...narrativeVariationConfig.vo, ...locationConfig.vo};

        const assets: HLF.AssetMapData[] = [];

        // VO
        addVO(assets, vo.all_items_found);
        addVO(assets, vo.exterior);
        // Background
        addBG(pID, assets, bg, HLF.AssetType.JPG);
        // music
        assets.push(new HLF.AudioAsset(locationConfig.bgm, HLF.AudioCategory.BGM));
        // Spritesheet contains Hiding Spots, Hidden Items, and NPC
        assets.push(
            ...HLF.AssetFactory(GameConfig.spritesheet_factory[pID], (i) => new HLF.TextureAtlasAsset(`${pID}-${i}`)));
        // Items
        itemIDs.forEach((id: ItemID, i) => {
            const item = locationConfig.items[id];
            addVO(assets, item.vo?.fact);
            addVO(assets, item.vo?.found);
            addVO(assets, item.vo?.returned);
            if (item.photoMode) {
                addSFX(pID, assets, "camera");
            }
        });

        return assets;
    }

    public init(pSize: PIXI.Point, pData: ILevelState) {
        super.init(pSize, pData); // calls onResize
        PubSub.publishSync(HLF.PUSH_KEYBOARD_LAYER, undefined);
        const config = GameConfig.locations[this.id];
        this.id = pData.id;

        // set up viewport
        this.initViewport(pSize, pData);
        this.initBackground(pData);
        this.initHidingSpots(pData);
        this.initVoiceover(pData);
        this.initMusic(pData);
        this.initParticles();
        this.hud.init(pSize, pData);

        this._subscriptions.push(
            PubSub.subscribe(Topics.HIDING_SPOT_SEARCHED, this.onMessageReceived.bind(this)),
            PubSub.subscribe(Topics.SCROLL_LEFT, this.onMessageReceived.bind(this)),
            PubSub.subscribe(Topics.SCROLL_RIGHT, this.onMessageReceived.bind(this)),
            PubSub.subscribe(Topics.ITEM_FOUND, this.onMessageReceived.bind(this)),
            PubSub.subscribe(Topics.ALL_ITEMS_FOUND, this.onMessageReceived.bind(this)),
            PubSub.subscribe(Topics.TOGGLE_HIT_AREA_RENDERER, this.onMessageReceived.bind(this)),
            PubSub.subscribe(Topics.HOME_BUTTON_PRESSED, this.onMessageReceived.bind(this)),
            PubSub.subscribe(Topics.LOCK_INPUT, this.onMessageReceived.bind(this)),
            PubSub.subscribe(Topics.UNLOCK_INPUT, this.onMessageReceived.bind(this)),
            PubSub.subscribe(Topics.HINT_BUTTON_PRESSED, this.onMessageReceived.bind(this)),
            PubSub.subscribe(Topics.RESTART_IDLE_TIMER, this.onMessageReceived.bind(this)),
            PubSub.subscribe(HLF.GAME_PAUSED, this.onMessageReceived.bind(this)),
            PubSub.subscribe(HLF.GAME_UNPAUSED, this.onMessageReceived.bind(this)),
        );
    }

    public onResize(pSize: PIXI.Point) {
        super.onResize(pSize);
        this.hud?.onResize(pSize);
        this.viewportWrapper.position.set(-pSize.x / 2, -pSize.y / 2);
        this.viewport?.resize(pSize.x, pSize.y);
        this.viewport?.clamp({
            left: 0 - this.margin.left,
            right: this.worldWidth - this.margin.left,
            top: 0,
            bottom: pSize.y,
        });
        this.updateScrollButtonsVisibility();
    }

    public destroy(pOptions?: PIXI.DestroyOptions | boolean) {
        TweenLite.killTweensOf(this);
        this._subscriptions.forEach(PubSub.unsubscribe);
        this.idleTimeline?.kill();
        this.itemFoundTimeline?.kill();
        this.hintTimeline?.kill();
        this.viewport?.removeAllListeners();
        this.clickParticles?.cleanup();
        this.clickParticles?.destroy();
        this.hintParticles?.cleanup();
        this.hintParticles?.destroy();
        super.destroy(pOptions);
    }

    public animateIn(pOnComplete: () => void) {
        super.animateIn(pOnComplete);
        this.hud?.animateIn();
        this.updateScrollButtonsVisibility();

        this.bgm?.setLooped(true);
        this.bgm?.setVolume(0.125);
        if (!(this.bgm?.isPlaying())) {
            this.bgm?.play();
        }

        const onVOComplete = (pDidPlayExteriorVO: boolean) => {
            if (!GameState.Instance.onboarding.complete) {
                this.hud.arrowButtons.HintBoth();
                Application.instance.voiceover.playVO([VO.ONBOARDING.O4, Constants.VO_SPACING], HLF.PlayMode.Append,
                    (didPlayArrows) => {
                        this.hud.PulseHintButton();
                        this.hud.arrowButtons.StopHints();
                        Application.instance.voiceover.playVO(
                            [VO.ONBOARDING.O3, Constants.VO_SPACING], HLF.PlayMode.Append,
                        );
                    });
            }

            this.idleTimeline = new TimelineMax();
            this.idleTimeline.delay(IDLE_DELAY);
            this.idleTimeline.call(this.playIdleVO.bind(this), undefined, undefined, IDLE_REPEAT_DELAY);
            this.idleTimeline.repeat(-1);

        };

        new TimelineLite().call(() => {
            if (this.vo?.exterior !== undefined && !Application.instance.skipVO) {
                Application.instance.voiceover.playVO(this.vo.exterior, HLF.PlayMode.Override, onVOComplete);
            }
            else {
                onVOComplete(false);
            }
        }).delay(1);
    }

    public update(pDeltaTime: number): void {
        super.update(pDeltaTime);
        this.hud?.luna?.update(pDeltaTime);
    }

    public animateOut(pOnComplete: () => void) {
        super.animateOut(pOnComplete);
        PubSub.publishSync(HLF.POP_KEYBOARD_LAYER, undefined);
    }
    public doCameraFlash() {
        Application.instance.audio.play("camera", 0.5, false, HLF.AudioCategory.SFX);
        const flash = new PIXI.Sprite(PIXI.Texture.WHITE);
        flash.width  = this._size.x;
        flash.height = this._size.y;
        flash.anchor.set(0.5);
        flash.position.set(0, 0);
        flash.alpha = 0;
        this.addChild(flash);
        new TimelineLite()
            .to(flash, 0.1, {pixi: {alpha: 1}})
            .to(flash, 1.5, {pixi: {alpha: 0}, ease: Expo.easeOut});
            // .call(pOnComplete);
    }

    public IsOffScreen(pHidingSpot: HidingSpot) {
        const offsetX = pHidingSpot.item?.offset.x ?? 0;
        const offsetY = pHidingSpot.item?.offset.y ?? 0;
        const margin = 150;
        const LUNA_DEAD_ZONE_HEIGHT = 150;
        const LUNA_DEAD_ZONE_WIDTH = 300;

        const PROGRESS_TRACKER_DEAD_ZONE_HEIGHT = this.hud.progressTracker.height;
        const PROGRESS_TRACKER_DEAD_ZONE_WIDTH =
            this.hud.progressTracker.width + this.hud.progressTracker.position.x + 80;

        let minX = this.viewport!.corner.x;
        const maxX = this.viewport!.corner.x + this.viewport!.worldScreenWidth + this.margin.right;
        const viewportCenter = (maxX + minX) / 2;
        const objectCenterX = pHidingSpot.x + offsetX;
        const objectCenterY = pHidingSpot.y + offsetY;
        const objectBottom = objectCenterY + margin;
        const objectLeft = objectCenterX - margin;
        const objectRight = objectCenterX + margin;

        if (objectBottom > (this.viewport!.worldScreenHeight - PROGRESS_TRACKER_DEAD_ZONE_HEIGHT)) {
            minX += PROGRESS_TRACKER_DEAD_ZONE_WIDTH;
        }
        else if (objectBottom > (this.viewport!.worldScreenHeight - LUNA_DEAD_ZONE_HEIGHT)) {
            minX += LUNA_DEAD_ZONE_WIDTH;
        }

        const offscreenLeft = !(objectLeft > minX);
        const offscreenRight = !(objectRight < maxX);
        let distance = objectCenterX - viewportCenter;
        if (offscreenLeft) {
            distance = objectLeft - minX;
            // console.log(pHidingSpot.name, {minX, objectLeft, objectRight, distance, offscreenLeft});

        }
        else if (offscreenRight) {
            distance = objectRight - maxX - this.margin.right;
            // console.log(pHidingSpot.name, {objectLeft, objectRight, maxX, distance, offscreenRight});
        }
        else {
            // console.log(pHidingSpot.name, {minX, objectLeft, objectRight, maxX});
        }

        return {
            left: offscreenLeft,
            right: offscreenRight,
            distance,
        };
    }

    protected playIdleVO() {
        this._clickCount = 0;
        this.idleTimeline?.kill();

        const voArray1 = [
            VO.LUNA.A12,
            VO.LUNA.A13,
            VO.LUNA.A14,
            VO.LUNA.A15,
        ];

        const voArray2 = [...(this.vo?.exterior_idle || [])];

        let voToPlay: string;
        if (voArray2.indexOf(this._lastIdleVO) > -1) {
            if (voArray1.indexOf(this._lastIdleDirectionVO) > -1) {
                voArray1.splice(voArray1.indexOf(this._lastIdleDirectionVO), 1);
            }
            voToPlay = HLF.ArrayUtils.random(voArray1);
            this._lastIdleDirectionVO = voToPlay;
        }
        else {
            if (voArray2.indexOf(this._lastIdleFactVO) > -1) {
                voArray2.splice(voArray2.indexOf(this._lastIdleFactVO), 1);
            }
            voToPlay = HLF.ArrayUtils.random(voArray2);
            this._lastIdleFactVO = voToPlay;
        }

        if (voToPlay !== undefined) {
            Application.instance.voiceover.playVO(
                voToPlay,
                HLF.PlayMode.New,
                (pDidPlay) => {
                    if (pDidPlay) {
                        this._lastIdleVO = voToPlay;
                    }
                    this.RestartIdleTimer();
                },
            );
        }
    }

    protected RestartIdleTimer() {
        this.idleTimeline?.restart(true, true);
    }

    protected doParticleEffect(pLocalPos: PIXI.Point) {
        this.clickParticles?.cleanup();
        this.clickParticles?.updateSpawnPos(pLocalPos.x, pLocalPos.y);
        this.clickParticles?.resetPositionTracking();
        this.clickParticles?.playOnce();
    }

    protected OnAllItemsFound(state: ILevelState) {
        // console.log("all items found", state.items);
        this.hud.homeButton.visible = false;
        this.hud.hintButton.visible = false;
        const onVOComplete = (pDidPlayAllItemsFound: boolean) => {
            if (this.itemFoundTimeline === undefined) {
                this.itemFoundTimeline = new TimelineMax();
            }
            this.itemFoundTimeline.call(() => {
                // console.log("go to int");
                GameState.Instance.GoToInterior();
            }, undefined, undefined, "+=0.1");
        };
        if (this.vo?.all_items_found !== undefined) {
            Application.instance.voiceover.playVO(this.vo.all_items_found, HLF.PlayMode.Append, onVOComplete);
        }
        else {
            onVOComplete(false);
        }
    }

    protected onMessageReceived(pTopic: string, pData: any) {
        switch (pTopic) {
            case Topics.ALL_ITEMS_FOUND:
                this.OnAllItemsFound(pData as ILevelState);
                break;
            case Topics.ITEM_FOUND:
                this.OnItemFound(pData as IItemState);
                break;
            case Topics.HIDING_SPOT_SEARCHED:
                this.OnHidingSpotSearched(pData as IHidingSpotState);
                break;
            case Topics.SCROLL_LEFT:
                this.ScrollLeft();
                break;
            case Topics.SCROLL_RIGHT:
                this.ScrollRight();
                break;
            case Topics.TOGGLE_HIT_AREA_RENDERER:
                this.ToggleHitAreaRenderer();
                break;
            case Topics.HOME_BUTTON_PRESSED:
                this.OnHomeButtonPressed();
                break;
            case Topics.LOCK_INPUT:
                this.LockInput();
                break;
            case Topics.UNLOCK_INPUT:
                this.UnlockInput();
                break;
            case Topics.HINT_BUTTON_PRESSED:
                this.OnHintButtonPressed();
                break;
            case Topics.RESTART_IDLE_TIMER:
                this.RestartIdleTimer();
                break;
            case HLF.GAME_PAUSED:
                this.OnGamePaused();
                break;
            case HLF.GAME_UNPAUSED:
                this.OnGameUnpaused();
                break;
        }
    }

    protected OnGamePaused() {
        this.idleTimeline?.kill();
    }

    protected OnGameUnpaused() {
        this.RestartIdleTimer();
    }

    protected OnHomeButtonPressed() {
        Application.instance.voiceover.stopVO();
        GameState.Instance.GoToLevelSelect();
    }

    protected onViewportEvent(event: string, viewport: Viewport) {
        this.updateScrollButtonsVisibility();
        if (event === "moved-end") {
            // this.maybeShowHint();
        }
        else if (event === "drag-start") {
            // this.hideHint();
        }
    }

    protected onClick(pLocalpos: PIXI.Point) {
        this.doParticleEffect(pLocalpos);
        this._clickCount++;
        if (this._clickCount >= TRIGGER_IDLE_AFTER_CLICKS) {
            this.playIdleVO();
        }
        // this.maybeShowHint(); // TODO: timer
        // TODO: does this sound ever play when we don't want it to?
        Application.instance.audio.play("blink", 1, false, HLF.AudioCategory.SFX);
    }

    protected ScrollLeft() {
        if (this.viewport === undefined) {
            throw new Error("viewport is undefined");
        }
        // this.hideHint();
        this._scroll(this.viewport.worldScreenWidth / 2 * -1);
    }

    protected ScrollRight() {
        if (this.viewport === undefined) {
            throw new Error("viewport is undefined");
        }
        this._scroll(this.viewport.worldScreenWidth / 2);
    }

    protected OnHidingSpotSearched(state: IHidingSpotState) {
        // console.log("hiding spot searched: ", state);
        // at the moment the only way to search an empty hiding spot is with keyboard
        if (state.itemID === undefined) { // empty
            Application.instance.audio.play("blink", 1, false, HLF.AudioCategory.SFX);
        }
        else if (this.hidingSpots[state.id]?.item?.found === true) { // already found
            Application.instance.audio.play("blink", 1, false, HLF.AudioCategory.SFX);
        }
    }

    protected OnItemFound(state: IItemState) {
        // console.log("item found: ", state);
        const config = GameConfig.locations[this.id];
        const itemConfig = config.items[state.id];
        const item = this.items[state.id];
        const allItemsFound = Object.values(this.items).every((i) => i !== undefined && i.found);

        this.hideHint();
        this.RestartIdleTimer();
        this._clickCount = -1;
        Application.instance.audio.play("found", 1, false, HLF.AudioCategory.SFX);

        const voQueue: Array<string|number> = [];
        voQueue.push(itemConfig.vo.found);
        voQueue.push(Constants.VO_SPACING);
        voQueue.push(HLF.ArrayUtils.random([
            VO.LUNA.A1,
            VO.LUNA.A2,
            VO.LUNA.A3,
            VO.LUNA.A4,
            VO.LUNA.A5,
            VO.LUNA.A6,
            VO.LUNA.A7,
            VO.LUNA.A8,
            VO.LUNA.A9,
        ]));

        if (itemConfig.vo.fact && !allItemsFound && !Application.instance.skipVO) {
            voQueue.push(Constants.VO_SPACING);
            voQueue.push(itemConfig.vo.fact);
        }

        Application.instance.voiceover.playVO(voQueue, HLF.PlayMode.Override);

        if (item === undefined) {
            throw new Error(`Can't find HiddenItem with id ${state.id}`);
        }
        const icon = this.addChild(makeSprite(this.id + "/" + itemConfig.sprite_hat, GameConfig.spritesheets[this.id]));

        if (item.photoMode) {
            this.doCameraFlash();
            this.tweenItemFromCenterToHat(icon);
        }
        else {
            this.tweenItemFromHidingSpotToHat(icon, item);
        }
        // TODO: cleanup
    }

    protected initHidingSpots(state: ILevelState) {
        const locationConfig = GameConfig.locations[state.id];
        const narrativeConfig = locationConfig.narratives[state.narrative];
        const narrativeVariationConfig = narrativeConfig.variations[state.narrativeVariation];
        // const itemconfigs = narrativeVariationConfig.hiddenitems;
        // const itemstates = state.items;

        // add hiding spots
        for (const hidingSpot of Object.values(state.hidingSpots)) {
            if (hidingSpot !== undefined) {
                const newSpot = new HidingSpot(
                    () => this.OnHidingSpotClicked(hidingSpot.id),
                    locationConfig.hidingspots[hidingSpot.id as HidingSpotID],
                    hidingSpot,
                    state,
                );
                this.viewportContentWrapper!.addChild(newSpot); // TODO: z-index
                PubSub.publishSync(HLF.REGISTER_FOCUSABLE, newSpot);
                if (hidingSpot.itemID !== undefined) {
                    this.items[hidingSpot.itemID] = newSpot.item;
                }
                this.hidingSpots[hidingSpot.id] = newSpot;
            }
        }
    }

    protected LockInput() {
        // console.log("lock input");
        this.viewport!.interactiveChildren = false;
        PubSub.publishSync(HLF.UNREGISTER_FOCUSABLES, Object.values(this.hidingSpots));
        PubSub.publishSync(HLF.CLEAR_FOCUS, undefined);
    }

    protected UnlockInput() {
        // console.log("unlock input");
        this.viewport!.interactiveChildren = true;
        this.updateScrollButtonsVisibility();
        PubSub.publishSync(HLF.REGISTER_FOCUSABLES, Object.values(this.hidingSpots));
    }

    protected OnHintButtonPressed() {
       this.maybeShowHint();
       this.RestartIdleTimer();
       this._clickCount = 0;
       Application.instance.analytics?.event("clickHelp", "lunas-adventures");
    }

    protected OnHidingSpotClicked(pID: HidingSpotID) {
        GameState.Instance.SearchHidingSpot(pID);
        this._clickCount++;
    }

    /**
     * scroll the viewport horizontally (use negative values for left)
     * @param pDistance relative distance in world units (pixels @ 1x)
     */
    protected _scroll(pDistance: number) {
        if (this.viewport === undefined) {
            throw new Error("viewport is undefined");
        }

        if (pDistance === 0) {
            return;
        }

        const oldX = this.viewport.corner.x;
        const minX = 0;
        const maxX = this.viewport.worldWidth - this.viewport.worldScreenWidth;

        let newX: number = Math.min(Math.max(oldX + pDistance, minX), maxX);
        let time: number = 500;

        if (oldX === newX) {
            const direction = Math.sign(pDistance);
            if (direction === 1) {
                newX = maxX + this.margin.right;
                time = 200;
            }
            else if (direction === -1) {
                newX = minX - this.margin.left;
                time = 200;
            }
            else {
                // console.warn("can't scroll left or right");
            }
        }

        this.viewport.snap(
            newX, 0,
            {
                topLeft: true,
                ease: "easeInOutSine",
                time,
                forceStart: true,
                removeOnComplete: true,
                interrupt: false,
                removeOnInterrupt: false,
            },
        );
    }

    protected initViewport(pSize: PIXI.Point, pData: ILevelState) {
        const locationConfig = GameConfig.locations[pData.id];
        this.worldWidth = locationConfig.exterior.width;
        this.margin = locationConfig.exterior.margin;

        this.viewport = this.viewportWrapper.addChild(
            new Viewport({
                worldWidth: this.worldWidth - this.margin.right - this.margin.left,
                worldHeight: pSize.y,
                screenWidth: pSize.x,
                screenHeight: pSize.y,
                ticker: Application.instance.ticker,
            })
            .drag({ direction: "x", wheel: false })
            .decelerate()
            .bounce({ time: 150 })
            .clamp({
                left: 0 - this.margin.left,
                right: this.worldWidth - this.margin.left,
                top: 0,
                bottom: pSize.y,
            }),
        );
        this.viewport.moveCenter(this.worldWidth / 2, 0);
        this.viewport.on("bounce-x-end", (data) => this.onViewportEvent("bounce-x-end", data));
        this.viewport.on("bounce-x-start", (data) => this.onViewportEvent("bounce-x-start", data));
        this.viewport.on("snap-start", (data) => this.onViewportEvent("snap-start", data));
        this.viewport.on("snap-end", (data) => this.onViewportEvent("snap-end", data));
        this.viewport.on("moved-end", (data) => this.onViewportEvent("moved-end", data));
        this.viewport.on("drag-start", (data) => this.onViewportEvent("drag-start", data.viewport));
        this.viewport.on("drag-end", (data) => this.onViewportEvent("drag-end", data.viewport));
        this.viewport.on("clicked", (data) => this.onClick(data.event.data.getLocalPosition(this)));
        // this.viewport.on("pointertap", ({ data }) => this.onClick(data.getLocalPosition(this)));
    }

    private initParticles() {
        const particleContainer = this.addChild(new PIXI.particles.ParticleContainer());
        // set up onclick particles
        this.clickParticles = new Emitter(particleContainer, [
            getTexture("sparkle-1", ss),
            getTexture("sparkle-2", ss),
            getTexture("sparkle-3", ss),
            getTexture("sparkle-4", ss),
        ], {
            alpha: {
                start: 1,
                end: 0,
            },
            scale: {
                start: 0.4,
                end: 0,
                minimumScaleMultiplier: 1,
            },
            color: {
                start: "#ffffff",
                end: "#ffffff",
            },
            speed: {
                start: 200,
                end: 50,
                minimumSpeedMultiplier: 1,
            },
            acceleration: {
                x: 0,
                y: 0,
            },
            maxSpeed: 0,
            startRotation: {
                min: 0,
                max: 360,
            },
            noRotation: false,
            rotationSpeed: {
                min: 0,
                max: 0,
            },
            lifetime: {
                min: 0.25,
                max: 0.75,
            },
            blendMode: "normal",
            frequency: 0.01,
            emitterLifetime: 0.1,
            maxParticles: 100,
            pos: {
                x: 0,
                y: 0,
            },
            addAtBack: false,
            spawnType: "circle",
            spawnCircle: {
                x: 0,
                y: 0,
                r: 10,
            },
        });
        this.clickParticles.emit = false;
        this.clickParticles.autoUpdate = true;
        this.hintParticles = new Emitter(particleContainer, [
            getTexture("sparkle-1", ss),
            getTexture("sparkle-2", ss),
            getTexture("sparkle-3", ss),
            getTexture("sparkle-4", ss),
        ], {
            alpha: {
                start: 0.5,
                end: 0.1,
            },
            scale: {
                start: 0.8,
                end: 0.1,
                minimumScaleMultiplier: 1,
            },
            color: {
                start: "#ffff00",
                end: "#ffff00",
            },
            speed: {
                start: -10,
                end: -10,
                minimumSpeedMultiplier: 1,
            },
            acceleration: {
                x: 0,
                y: 0,
            },
            maxSpeed: 0,
            startRotation: {
                min: -90,
                max: 90,
            },
            noRotation: false,
            rotationSpeed: {
                min: 0,
                max: 0,
            },
            lifetime: {
                min: 1,
                max: 1,
            },
            blendMode: "normal",
            frequency: 0.001,
            emitterLifetime: -1,
            maxParticles: 1500,
            pos: {
                x: 0,
                y: 0,
            },
            addAtBack: false,
            spawnType: "ring",
            spawnCircle: {
                x: 0,
                y: 0,
                r: 500,
                minR: 500,
            },
        });
        this.hintParticles.emit = false;
        this.hintParticles.autoUpdate = true;
    }

    private initVoiceover(state: ILevelState) {
        const locationConfig = GameConfig.locations[state.id];
        const narrativeConfig = locationConfig.narratives[state.narrative];
        const narrativeVariationConfig = narrativeConfig.variations[state.narrativeVariation];
        this.vo = {...locationConfig.vo, ...narrativeConfig.vo, ...narrativeVariationConfig.vo};
    }

    private initBackground(state: ILevelState) {
        const locationConfig = GameConfig.locations[state.id];
        this.viewportContentWrapper = this.viewport!.addChild(new PIXI.Container());
        this.viewportContentWrapper.position.x = -this.margin.left;
        this.addBG(locationConfig.exterior.bg, this.viewportContentWrapper, state);
    }
    private initMusic(state: ILevelState) {
        const locationConfig = GameConfig.locations[state.id];
        this.bgm = Application.instance.audio.getAudioTrack(locationConfig.bgm, HLF.AudioCategory.BGM);
    }
    private tweenItemFromCenterToHat(
        icon: PIXI.Sprite,
    ) {
        const hatInnerWidth = 40;
        const hatInnerHeight = 40;
        const aspect = (icon.height / icon.width);
        let targetWidth;
        let targetHeight;
        if (aspect > 1) {
            targetHeight = hatInnerHeight;
            targetWidth = targetHeight / aspect;
        }
        else {
            targetWidth = hatInnerWidth;
            targetHeight = targetWidth * aspect;
        }

        icon.scale.set(2, 2);
        if (this.itemFoundTimeline === undefined) {
            this.itemFoundTimeline = new TimelineMax();
            this.itemFoundTimeline.autoRemoveChildren = true;
        }
        else if (this.itemFoundTimeline.isActive()) { // two items found in quick succession
            this.itemFoundTimeline.seek("+=0", true); // skip to end
            this.itemFoundTimeline.clear();
            this.itemFoundTimeline.restart();
        }

        const t = this.itemFoundTimeline;

        // 0s->0.1s scale down, in place
        t.to(icon, 0.6, {
            pixi: {
                rotation: 0,
                scaleX: 1,
                scaleY: 1,
            },
        }, t.time());

        // 2s->3s tween to hat position
        t.call(() => {
            const p2 = this.toLocal(this.hud!.luna.getHatPosition());
            new TimelineMax().to(icon.position, 1, {
                bezier: {
                    type: "thru",
                    values: [
                        { x: p2.x / 2, y: -100 },
                        { x: p2.x, y: p2.y - 20 },
                    ],
                    curviness: 2,
                },
                ease: Sine.easeOut,
            }).call(() => {
                Application.instance.audio.play("into-hat-2", 1, false, HLF.AudioCategory.SFX);
            }).to(icon, 0.5, {
                pixi: {
                    positionY: p2.y,
                },
            });
        }, undefined, undefined, t.time() + 2);
        // 2.25s-3s scale down
        t.to(icon, 1, {
            pixi: {
                width: targetWidth,
                height: targetHeight,
            },
            ease: "power4.out",
        }, t.time() + 2);
        // 3.25s-3.35s fade out
        t.to(icon, 0.1, { pixi: { alpha: 0 } }, t.time() + 3.25);
        t.call(() => {
            icon.destroy();
        });
    }

    private tweenItemFromHidingSpotToHat(
        icon: PIXI.Sprite,
        item: HiddenItem,
    ) {
        const hatInnerWidth = 40;
        const hatInnerHeight = 40;
        const aspect = (icon.height / icon.width);
        let targetWidth;
        let targetHeight;
        if (aspect > 1) {
            targetHeight = hatInnerHeight;
            targetWidth = targetHeight / aspect;
        }
        else {
            targetWidth = hatInnerWidth;
            targetHeight = targetWidth * aspect;
        }
        // start position, scale 0
        const p0 = icon.toLocal(item.toGlobal(item.offset));
        const iconBottom = p0.y + (icon.height / 2);
        const iconTop = p0.y - (icon.height / 2);
        const iconLeft = p0.x - (icon.width / 2);
        const iconRight = p0.x + (icon.width / 2);
        if (iconBottom > this._size.y / 2) {
            console.warn("bottom edge of item is cut off");
            p0.y = (this._size.y / 2) - (icon.height / 2);
        }
        if (iconTop < -this._size.y / 2) {
            console.warn("top edge of item is cut off");
            p0.y = (-this._size.y / 2) + (icon.height / 2);
        }
        if (iconLeft < -this._size.x / 2) {
            console.warn("left edge of item is cut off");
            p0.x = (-this._size.x / 2) + (icon.width / 2);
        }
        if (iconRight > this._size.x / 2) {
            console.warn("right edge of item is cut off");
            p0.x = (this._size.x / 2) - (icon.width / 2);
        }
        icon.position.copy(p0);
        icon.scale.set(item.scale.x, item.scale.y);
        icon.rotation = item.sprite.rotation;
        if (this.itemFoundTimeline === undefined) {
            this.itemFoundTimeline = new TimelineMax();
            this.itemFoundTimeline.autoRemoveChildren = true;
        }
        else if (this.itemFoundTimeline.isActive()) { // two items found in quick succession
            this.itemFoundTimeline.seek("+=0", true);
            this.itemFoundTimeline.clear();
            this.itemFoundTimeline.restart();
        }

        const t = this.itemFoundTimeline;
        const flipX = Math.sign(item.scale.x);
        t
            // squish
            .to(icon, 5 / 30, {pixi: {
                scaleX: 1.1 * flipX,
                scaleY: 0.8,
                rotation: 0,
            },
            })
            // squash
            .to(
                icon,
                1,
                { pixi: {
                    scaleX: 1 * flipX,
                    scaleY: 1,
                }, ease: Elastic.easeOut },
            )
        ;
        // 2s->3s tween to hat position
        t.call(() => {
            const p2 = this.toLocal(this.hud!.luna.getHatPosition());
            new TimelineMax().to(icon.position, 1, {
                bezier: {
                    type: "thru",
                    values: [
                        { x: (p0.x + p2.x) / 2, y: Math.min(p0.y, -100) },
                        { x: p2.x, y: p2.y - 20 },
                    ],
                    curviness: 2,
                },
                ease: Sine.easeOut,
            }).call(() => {
                Application.instance.audio.play("into-hat-2", 1, false, HLF.AudioCategory.SFX);
            }).to(icon, 0.5, {
                pixi: {
                    positionY: p2.y,
                },
            });
        }, undefined, undefined, t.time() + 2);
        // 2.25s-3s scale down
        t.to(icon, 1, {
            pixi: {
                width: targetWidth,
                height: targetHeight,
            },
            ease: "power4.out",
        }, t.time() + 2);
        // 3.25s-3.35s fade out
        t.to(icon, 0.1, { pixi: { alpha: 0 } }, t.time() + 3.25);
        t.call(() => {
            icon.destroy();
        });
    }

    private ToggleHitAreaRenderer() {
        Application.instance.hitAreaRenderer.active = !Application.instance.hitAreaRenderer.active;
    }

    private addBG(pBG: BGConfig, pTarget: PIXI.Container, state: ILevelState) {
        if (Array.isArray(pBG)) {
            for (const _bg of pBG) {
                const newsprite = makeSprite(state.id + "/" + _bg.filename);
                newsprite.anchor.set(0, 0);
                newsprite.position.set(_bg.x, _bg.y);
                this.bg.addChild(newsprite);
            }
        }
        else {
            const newsprite = makeSprite(state.id + "/" + pBG.filename);
            this.bg.addChild(newsprite);
        }

        pTarget.addChild(this.bg);
    }

    private updateScrollButtonsVisibility() {
        if (this.viewport === undefined) {
            return;
        }
        const x = this.viewport.corner.x;
        const minX = 0;
        const maxX = this.viewport.worldWidth - this.viewport.worldScreenWidth;
        const canScrollLeft = x > minX;
        const canScrollRight = x < maxX;
        if (this.hud !== undefined) {
            const {leftButton, rightButton} = this.hud.arrowButtons;
            leftButton.visible = canScrollLeft;
            leftButton.interactive = canScrollLeft;
            rightButton.visible = canScrollRight;
            rightButton.interactive = canScrollRight;
            if (!canScrollLeft) {
                PubSub.publishSync(HLF.UNREGISTER_FOCUSABLE, leftButton);
            }
            else {
                PubSub.publishSync(HLF.REGISTER_FOCUSABLE, leftButton);
            }
            if (!canScrollRight) {
                PubSub.publishSync(HLF.UNREGISTER_FOCUSABLE, rightButton);
            }
            else {
                PubSub.publishSync(HLF.REGISTER_FOCUSABLE, rightButton);
            }
        }
    }

    private maybeShowHint() {
        let offscreenHidingSpot;
        let os = {left: false, right: false, distance: 0};

        const onscreenHidingSpot = Object.values(this.hidingSpots).find((e, i, a) => {
            if (!e) { return false; }
            const notFound = e?.item?.found === false;
            if (notFound) {
                os = this.IsOffScreen(e);
                offscreenHidingSpot = e;
            }
            return notFound && (!os.left && !os.right); // onscreen only
        });
        if (onscreenHidingSpot) {
            this.showHint(onscreenHidingSpot, os);
        }
        else if (offscreenHidingSpot) {
            this.showHint(offscreenHidingSpot, os);
        }
    }

    private showHint(hidingSpot: HidingSpot, offscreen: {left: boolean, right: boolean, distance: number}) {
        // const delayBeforeHint = 5;
        // console.log(hidingSpot.name, hidingSpot.item?.name, offscreen);

        this.hintTimeline?.kill();
        this.hintTimeline = new TimelineLite();

        if (offscreen.left && this.hud?.arrowButtons.leftButton.visible) {
            // this.hud?.arrowButtons.HintLeft();
            this._scroll(offscreen.distance);
        }
        else if (offscreen.right && this.hud?.arrowButtons.rightButton.visible) {
            // this.hud?.arrowButtons.HintRight();
            this._scroll(offscreen.distance);
        }

        // this.hintParticles?.resetPositionTracking();

        this.hintTimeline.call(() => {
            this.hintParticles?.updateSpawnPos(
                hidingSpot.position.x + hidingSpot.item!.offset.x,
                hidingSpot.position.y + hidingSpot.item!.offset.y,
            );

            this.hintParticles!.parent = this.viewportContentWrapper!;
            this.hintParticles!.emit = true;
        })
        // cloud becomes a ring
        .fromTo(
            this.hintParticles!.spawnCircle,
            1.5,
            {radius: 500, minRadius: 500},
            {radius: 100, minRadius: 100},
        )
        // .delay(delayBeforeHint);
        ;
    }

    private hideHint() {
        this.hud?.arrowButtons.StopHints();
        this.hintParticles!.emit = false;
        this.hintTimeline?.kill();
    }
}
